import { useEffect, useRef, useState } from "react";
import type { RunInFlightRefusal } from "../hooks/useRunInFlightRefusal";

type Props = {
  title: string;
  canEdit: boolean;
  onSave: (next: string) => Promise<void>;
  className?: string;
  // 0159 F1: the product's shared in-flight refusal rule
  // (useRunInFlightRefusal), from the caller. A save the server refused under
  // a live run lease is handed to it, and its line - shown only until the
  // server's answer about the product's run changes - stands in for the
  // thrown message. Every other failure reads as before.
  inFlightRefusal?: RunInFlightRefusal;
};

// 0028: inline-edit affordance for the product title. Reused on the
// product page breadcrumb AND the dashboard row, both of which sit
// inside a Link-wrapped row — so the edit-mode interactions
// stopPropagation() to keep the row from navigating when the founder
// clicks the pencil or the input.
export function EditableTitle({ title, canEdit, onSave, className, inFlightRefusal }: Props) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(title);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  // Saving guard via ref (not state) — prevents the double-submit
  // race when Enter triggers commit() and the disabled-attr-driven
  // blur also fires commit() before the first async resolves.
  const savingRef = useRef(false);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  // Sync external title changes into the draft when NOT editing —
  // covers initial mount, refetch after navigation, and the
  // post-PATCH cache invalidation. ANALYZE itself doesn't refine
  // an already-authored title (planner.ApplyTitle's stickiness),
  // so external title changes here are mostly cache-driven.
  useEffect(() => {
    if (!editing) setDraft(title);
  }, [title, editing]);

  // Clear the inline failure: this component's own, and an in-flight refusal.
  const clearErr = () => {
    setErr(null);
    inFlightRefusal?.clear();
  };

  const commit = async () => {
    if (savingRef.current) return;
    const next = draft.trim();
    if (!next || next === title) {
      setEditing(false);
      setDraft(title);
      clearErr();
      return;
    }
    if (next.length > 80) {
      setErr("Title must be ≤80 characters");
      return;
    }
    savingRef.current = true;
    setSaving(true);
    clearErr();
    try {
      await onSave(next);
      setEditing(false);
    } catch (e) {
      if (!inFlightRefusal?.note(e)) setErr((e as Error).message ?? "Failed to save");
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  // Read modes use a flex layout so the title text can truncate
  // (with ellipsis) WITHOUT clipping the trailing ✎ button. Inner
  // <span> takes the truncate; the button is shrink-0 and always
  // visible. Parent passes max-width via className; this wrapper
  // fills it via max-w-full + min-w-0.
  const layoutClass = "inline-flex items-baseline max-w-full min-w-0";
  // The inline line beside the input: this component's own failure, else an
  // in-flight refusal the shared rule still holds.
  const line = err ?? inFlightRefusal?.line ?? null;

  if (!canEdit) {
    return (
      <span className={`${layoutClass} ${className ?? ""}`}>
        <span className="truncate min-w-0">{title}</span>
      </span>
    );
  }
  if (!editing) {
    return (
      <span className={`${layoutClass} ${className ?? ""}`}>
        <span className="truncate min-w-0">{title}</span>
        <button
          type="button"
          onClick={(e) => {
            // Prevent any wrapping <Link> from navigating.
            e.preventDefault();
            e.stopPropagation();
            setEditing(true);
          }}
          className="ml-2 shrink-0 text-ink-500 hover:text-ink-200 transition-colors"
          title="Edit title"
          aria-label="Edit title"
        >
          ✎
        </button>
      </span>
    );
  }
  return (
    <span className={`inline-flex items-baseline ${className ?? ""}`}>
      <input
        ref={inputRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        onBlur={() => {
          void commit();
        }}
        onKeyDown={(e) => {
          // Stop bubbling so global shortcuts / parent handlers don't
          // consume keystrokes meant for the input.
          e.stopPropagation();
          if (e.key === "Enter") {
            e.preventDefault();
            void commit();
          } else if (e.key === "Escape") {
            e.preventDefault();
            setEditing(false);
            setDraft(title);
            clearErr();
          }
        }}
        disabled={saving}
        maxLength={80}
        className="bg-ink-900 border border-ink-700 rounded px-2 py-0.5 text-inherit focus:border-accent focus:outline-none"
      />
      {line && <span className="ml-2 shrink-0 text-danger text-xs">{line}</span>}
    </span>
  );
}
